import React from "react";
import { Box, Button, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import FormulaRiskMatrix from "./FormulaRiskMatrix";
import {
  buildFormulaRiskReport,
  downloadTextFile,
  serializeFormulaRiskReportJson,
  serializeFormulaRiskReportMarkdown,
} from "../platform/riskReport";
import { PageRoot, SectionCard, StatusChip, SourceCitation } from "./ui/PlatformPrimitives";

const sevBarOpacity = (s) =>
  s === "critical" || s === "high" || s === "error" ? 1
  : s === "medium" || s === "warning" ? 0.6 : 0.3;

const IssueRow = ({ issue }) => (
  <Paper
    sx={{
      p: { xs: 2, md: 2.5 },
      display: "flex",
      alignItems: "center",
      gap: 2,
      position: "relative",
      overflow: "hidden",
      borderRadius: 2, // 16px — outer surface tier
    }}
  >
    {/* The 3px severity rail sits above the specular pseudo so it still
        reads against translucent glass. zIndex bumps it past the ::before.
        Severity tints use the rgba palette so the rail composes with glass. */}
    <Box
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        bgcolor: issue.blocking ? "rgba(153,27,27,0.85)" : "rgba(146,64,14,0.75)",
        opacity: sevBarOpacity(issue.severity),
        zIndex: 1,
      }}
    />
    <Chip
      label={issue.severity}
      size="small"
      sx={{
        ml: 0.5,
        minWidth: 60,
        textTransform: "capitalize",
        fontWeight: 600,
        borderRadius: 999,
        // Blocking severity uses the navy brand chip (allowlisted #1B2A4A);
        // non-blocking inherits the theme's glass-inset chip so it composes
        // with the parent glass surface.
        ...(issue.blocking
          ? { bgcolor: "#1B2A4A", color: "#FFFFFF", border: "none" }
          : {}),
        zIndex: 1,
      }}
    />
    <Typography variant="body2" sx={{ flex: 1, position: "relative", zIndex: 1, fontWeight: 500, color: "text.primary" }}>{issue.message}</Typography>
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <SourceCitation source={issue.source} sourceUrl={issue.sourceUrl} sourceQuote={issue.sourceQuote} sourceQuotes={issue.sourceQuotes} highlight={issue.highlight} />
    </Box>
  </Paper>
);

const FormulaDetails = ({ product }) => {
  const theme = useTheme();
  if (!product?.compliance?.activeFormula) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            // Concentric pill (inset tier is otherwise 12; circular avatar
            // uses 999 for a pure round).
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
            // Inset glass tier — replaces the previously hardcoded translucent
            // background / blur / hairline border (rule 9).
            background: theme.glass.inset.background,
            backdropFilter: theme.glass.inset.backdropFilter,
            WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
            border: theme.glass.inset.border,
            boxShadow: theme.glass.inset.boxShadow,
          }}
        >
          <Typography variant="h5" sx={{ color: "text.disabled" }}>&#9883;</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Select a product to view formula details
        </Typography>
      </Box>
    );
  }

  const { activeFormula, labelDrafts, pifDraft, jurisdictions } = product.compliance;
  const results = activeFormula.ifraResults || [];
  const riskReport = buildFormulaRiskReport({
    product,
    compliance: product.compliance,
    inputText: activeFormula.inputText,
  });
  const reportFilenameBase = `${product.name || "formula"}-risk-report`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const downloadReport = (format) => {
    if (format === "json") {
      downloadTextFile({
        filename: `${reportFilenameBase}.json`,
        content: serializeFormulaRiskReportJson(riskReport),
        type: "application/json",
      });
      return;
    }
    downloadTextFile({
      filename: `${reportFilenameBase}.md`,
      content: serializeFormulaRiskReportMarkdown(riskReport),
      type: "text/markdown",
    });
  };

  return (
    <PageRoot>
      <Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">{activeFormula.name}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {activeFormula.source} &middot; {new Date(activeFormula.createdAt).toLocaleDateString()}
              &nbsp;&middot; {results.length} ingredients
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={`/labeling?product=${product.id}`}
              startIcon={<LabelOutlinedIcon sx={{ fontSize: 14 }} />}
            >
              Generate label
            </Button>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />} onClick={() => downloadReport("markdown")}>
              MD
            </Button>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />} onClick={() => downloadReport("json")}>
              JSON
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {[
          { label: "IFRA", status: jurisdictions.IFRA.status },
          { label: "EU", status: jurisdictions.EU.status },
          { label: "US", status: jurisdictions.US.status },
          { label: "PIF", status: pifDraft.ready ? "Ready" : "Review" },
        ].map((item) => (
          <Grid key={item.label} size={{ xs: 6, sm: 3 }}>
            <Paper sx={{ p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", position: "relative", zIndex: 1 }}>{item.label}</Typography>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <StatusChip status={item.status} />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <FormulaRiskMatrix report={riskReport} />

      {["EU", "US"].map((jur) => {
        const section = jurisdictions[jur];
        if (!section?.issues?.length) return null;
        const allergenIssues = section.issues.filter((i) => i.code === "EU_LABEL_ALLERGEN_REVIEW");
        const otherIssues = section.issues.filter((i) => i.code !== "EU_LABEL_ALLERGEN_REVIEW");

        return (
          <SectionCard key={jur} title={`${jur} — ${section.issues.length} issue${section.issues.length !== 1 ? "s" : ""}`}>
            <Stack spacing={2}>
              {otherIssues.map((issue, idx) => (
                <IssueRow key={`${issue.code}-${idx}`} issue={issue} />
              ))}
              {allergenIssues.length > 0 && (
                <Paper sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2, position: "relative", overflow: "hidden", borderRadius: 2 }}>
                  <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: "rgba(146,64,14,0.75)", opacity: 0.4, zIndex: 1 }} />
                  <Typography variant="body2" sx={{ flex: 1, ml: 0.5, fontWeight: 500, position: "relative", zIndex: 1, color: "text.primary" }}>
                    {allergenIssues.length} allergen{allergenIssues.length > 1 ? "s" : ""} to declare: {allergenIssues.map((i) => i.message.split(" — ")[0]).join(", ")}
                  </Typography>
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <SourceCitation source={allergenIssues[0].source} sourceUrl={allergenIssues[0].sourceUrl} sourceQuote={allergenIssues[0].sourceQuote} sourceQuotes={allergenIssues[0].sourceQuotes} highlight={allergenIssues[0].highlight} />
                  </Box>
                </Paper>
              )}
            </Stack>
          </SectionCard>
        );
      })}
    </PageRoot>
  );
};

export default FormulaDetails;
